import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button, Checkbox } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, ListToolbar, SavedViewMenu } from '../organisms';
import type { FormValues } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { STATE_FILTER_OPTIONS } from '../lib/us-states';

/**
 * ParcelsList — /v2/parcels
 *
 * Schema-driven columns: Account · Name · Type · Current/Proposed value ·
 * Under appeal? · Status. Row click → /parcels/:id.
 *
 * Server-side search / sort / paginate (PRD 06.1): the debounced search box,
 * the status / RE-PP / under-appeal filters, the sortable column headers, and
 * the pager all drive `GET /api/parcels?q&status&re_pp&under_appeal&sort&dir&page&limit`
 * (org + SoD row-scope enforced server-side). No client-side filtering/slicing.
 *
 * Saved views (PRD 06.2): the SavedViewMenu persists & restores the full
 * filter/sort envelope ({ q, status, re_pp, under_appeal, sort, dir }) against
 * the real /api/saved-views store; applying a view writes back onto this list's
 * server-side state.
 *
 * Phase 11 Block 7 PR 4 (2026-05-12); rewired for server-side list (06.1) + saved views (06.2).
 */

interface Parcel {
  id: string;
  account_number: string;
  alternate_id: string | null;
  name: string | null;
  property_id: string;
  status: 'active' | 'inactive' | 'in_setup' | 'historical' | 'sold' | 'sold_after_value' | 'unknown' | 'other';
  re_pp: 'real_estate' | 'personal_property';
  under_appeal: boolean;
  appeal_date: string | null;
  current_market_value: number | null;
  proposed_market_value: number | null;
  owner_name: string | null;
  state: string | null;
}

const statusVariant: Record<Parcel['status'], 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  active: 'success',
  inactive: 'neutral',
  in_setup: 'warning',
  historical: 'info',
  sold: 'neutral',
  sold_after_value: 'neutral',
  unknown: 'neutral',
  other: 'neutral',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'in_setup', label: 'In Setup' },
  { value: 'historical', label: 'Historical' },
  { value: 'sold', label: 'Sold' },
];

const RE_PP_OPTIONS = [
  { value: 'real_estate', label: 'Real estate' },
  { value: 'personal_property', label: 'Personal property' },
];

const APPEAL_OPTIONS = [
  { value: 'true', label: 'Under appeal' },
  { value: 'false', label: 'Not under appeal' },
];

const SETTLED_OPTIONS = [
  { value: 'true', label: 'Settled' },
  { value: 'false', label: 'Not settled' },
];

// Assignment scope (dashboard "My Appeals"/"My Properties" deep-links): the only
// value the backend recognizes is `me`, which narrows to the requesting user's own
// book via agentScopeClause — even for a firm-wide admin who would otherwise see
// the whole org. Selecting it (or arriving via ?assigned=me) restricts the list.
const ASSIGNED_OPTIONS = [
  { value: 'me', label: 'Assigned to me' },
];

// Roles that may create org-scoped ("shared") saved views (admin-tier, matching
// the saved-views router's ADMIN_TIER_LEVEL). Also gates the shared checkbox.
const ADMIN_TIER = new Set(['super_admin', 'admin', 'administrator']);

interface CurrentUser { id: string; role: string }

// A custom-field definition (generic-CRUD table custom_field_definitions).
interface CustomFieldDef {
  id: string;
  field_key: string;
  label: string;
  data_type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  is_active: boolean | number;
}

// A row from GET /api/custom-fields/query — one (entity, value) pair for a field.
interface CustomFieldValueRow {
  entity_type: string;
  entity_id: string;
  value: string | null;
  field_key: string;
}

export function ParcelsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Deep-link filters (Dashboard "Appeal pipeline" donut + KPI cards): the list
  // opens pre-filtered from ?status / ?under_appeal / ?settled on the URL. Read
  // once at mount via lazy state initializers; the toolbar owns them thereafter.
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [rePpFilter, setRePpFilter] = useState(() => searchParams.get('re_pp') ?? '');
  const [appealFilter, setAppealFilter] = useState(() => searchParams.get('under_appeal') ?? '');
  const [settledFilter, setSettledFilter] = useState(() => searchParams.get('settled') ?? '');
  const [stateFilter, setStateFilter] = useState(() => searchParams.get('state') ?? '');
  // Deep-link county filter — the Appeals worklist "County progress" rows drill here
  // (/parcels?county=Bexar County). A plain server-side equality filter on the real
  // parcels.county column (crud SCALAR_FILTERS).
  const [countyFilter, setCountyFilter] = useState(() => searchParams.get('county') ?? '');
  // B2 (Pepco Jul-15): filter the portfolio by ASSESSOR — the one filter David named
  // that the list did not have. Server-side equality on parcels.assessor_id via the
  // crud SCALAR_FILTERS, the same path state/county already take.
  const [assessorFilter, setAssessorFilter] = useState(() => searchParams.get('assessor_id') ?? '');
  // "My Appeals" / "My Properties" deep-link: ?assigned=me scopes to my book.
  const [assignedFilter, setAssignedFilter] = useState(() => searchParams.get('assigned') ?? '');
  // Custom-field columns (07.3): which field_keys the operator has added as
  // extra columns, and whether the picker is open.
  const [customCols, setCustomCols] = useState<string[]>([]);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const list = useServerList<Parcel>({
    resource: 'parcels',
    queryKey: ['parcels-list'],
    defaultPageSize: 50,
    defaultSort: { id: 'account_number', desc: false },
    filters: { status: statusFilter, re_pp: rePpFilter, under_appeal: appealFilter, settled: settledFilter, state: stateFilter, county: countyFilter, assessor_id: assessorFilter, assigned: assignedFilter },
  });

  // B2: the assessor options. Fetched once and cached — an appraisal-district list is
  // reference data that changes yearly, not per session. Sorted by name so the picker
  // reads alphabetically rather than in insertion order.
  const assessorsQuery = useQuery<Array<{ id: string; name: string; state: string | null }>>({
    queryKey: ['assessors-for-filter'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<Array<{ id: string; name: string; state: string | null }> | { rows: Array<{ id: string; name: string; state: string | null }> }>(
        '/api/assessors?limit=200', signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    staleTime: 10 * 60_000,
  });
  const assessorOptions = useMemo(() => {
    const rows = [...(assessorsQuery.data ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    // The state suffix disambiguates same-named districts across states, which a
    // multi-state firm hits immediately.
    return rows.map(a => ({ value: a.id, label: a.state ? `${a.name} (${a.state})` : a.name }));
  }, [assessorsQuery.data]);

  // Current user powers the SavedViewMenu's share/delete affordances.
  const meQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    staleTime: 5 * 60_000,
  });
  const canShare = ADMIN_TIER.has(meQuery.data?.role ?? '');

  // The current filter/sort envelope a saved view stores. Applying a view writes
  // these back onto the server-side list state below.
  const currentConfig = useMemo(
    () => ({
      q: list.searchInput,
      status: statusFilter,
      re_pp: rePpFilter,
      under_appeal: appealFilter,
      settled: settledFilter,
      state: stateFilter,
      county: countyFilter,
      assessor_id: assessorFilter,
      assigned: assignedFilter,
      sort: list.sorting[0]?.id ?? 'account_number',
      dir: list.sorting[0]?.desc ? 'desc' : 'asc',
    }),
    [list.searchInput, statusFilter, rePpFilter, appealFilter, settledFilter, stateFilter, countyFilter, assessorFilter, assignedFilter, list.sorting],
  );

  function applyView(config: Record<string, unknown>) {
    list.setSearchInput(typeof config.q === 'string' ? config.q : '');
    setStatusFilter(typeof config.status === 'string' ? config.status : '');
    setRePpFilter(typeof config.re_pp === 'string' ? config.re_pp : '');
    setAppealFilter(typeof config.under_appeal === 'string' ? config.under_appeal : '');
    setSettledFilter(typeof config.settled === 'string' ? config.settled : '');
    setStateFilter(typeof config.state === 'string' ? config.state : '');
    setCountyFilter(typeof config.county === 'string' ? config.county : '');
    setAssessorFilter(typeof config.assessor_id === 'string' ? config.assessor_id : '');
    setAssignedFilter(typeof config.assigned === 'string' ? config.assigned : '');
    const sortId = typeof config.sort === 'string' ? config.sort : 'account_number';
    list.setSorting([{ id: sortId, desc: config.dir === 'desc' }]);
    // useServerList resets to page 1 whenever search/filter/sort change.
  }

  // Custom-field definitions available to add as columns. Definitions ride the
  // generic-CRUD table (org-scoped); a client- or property-scoped field both
  // resolve down to parcels, so every active definition can surface here. The
  // envelope is { rows, total } when a limit is passed — unwrap defensively.
  const cfDefsQuery = useQuery<CustomFieldDef[]>({
    queryKey: ['parcel-custom-field-defs'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<CustomFieldDef[] | { rows: CustomFieldDef[] }>(
        '/api/custom-field-definitions?limit=200', signal,
      );
      const rows = Array.isArray(resp) ? resp : resp.rows;
      return rows.filter(d => d.is_active === true || d.is_active === 1);
    },
  });
  const availableDefs = cfDefsQuery.data ?? [];
  const defByKey = useMemo(() => {
    const m = new Map<string, CustomFieldDef>();
    for (const d of availableDefs) m.set(d.field_key, d);
    return m;
  }, [availableDefs]);

  // Real custom-field VALUES for the selected columns: GET /api/custom-fields/query
  // returns every (parcel, value) pair for a field_key across the org (≤500),
  // which we index by parcel id and join client-side onto the current page.
  const selectedKey = [...customCols].sort().join(',');
  const cfValuesQuery = useQuery<Record<string, Record<string, string | null>>>({
    queryKey: ['parcel-custom-field-values', selectedKey],
    enabled: customCols.length > 0,
    queryFn: async ({ signal }) => {
      const byField: Record<string, Record<string, string | null>> = {};
      await Promise.all(customCols.map(async (fieldKey) => {
        const rows = await api.get<CustomFieldValueRow[]>(
          `/api/custom-fields/query?entity_type=parcels&field_key=${encodeURIComponent(fieldKey)}`,
          signal,
        );
        const map: Record<string, string | null> = {};
        for (const r of rows) map[r.entity_id] = r.value;
        byField[fieldKey] = map;
      }));
      return byField;
    },
  });

  function toggleCustomCol(fieldKey: string) {
    setCustomCols(prev => prev.includes(fieldKey)
      ? prev.filter(k => k !== fieldKey)
      : [...prev, fieldKey]);
  }

  const columns = useMemo<ColumnDef<Parcel>[]>(() => [
    {
      accessorKey: 'account_number',
      header: 'Account #',
      cell: ({ row, getValue }) => (
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
            {getValue() as string}
          </div>
          {row.original.alternate_id && (
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              alt: {row.original.alternate_id}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue, row }) => (getValue() as string) || row.original.owner_name || '—',
    },
    {
      accessorKey: 'state',
      header: 'State',
      enableSorting: true,   // `state` is a real column → server-side ORDER BY (crud.js)
      cell: ({ getValue }) => {
        const s = getValue() as string | null;
        if (!s) return <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
        // Click a state cell to browse every parcel in that state — PRD 2.1's
        // "all-parcels-in-a-state in two clicks". stopPropagation so it applies
        // the filter without also firing the row's onRowClick → parcel detail.
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setStateFilter(s); }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'var(--brand-primary)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {s}
          </button>
        );
      },
    },
    {
      accessorKey: 're_pp',
      header: 'Type',
      cell: ({ getValue }) => (getValue() === 'real_estate' ? 'Real estate' : 'Personal property'),
    },
    {
      accessorKey: 'current_market_value',
      header: 'Current value',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    {
      accessorKey: 'proposed_market_value',
      header: 'Proposed',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    {
      accessorKey: 'under_appeal',
      header: 'Appeal',
      cell: ({ getValue }) => {
        const v = getValue() as boolean;
        return v ? <Badge variant="warning" dot>Under appeal</Badge> : <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = String(getValue() ?? 'active') as Parcel['status'];
        return <Badge variant={statusVariant[s] ?? 'neutral'} dot>{s.replace(/_/g, ' ')}</Badge>;
      },
    },
    // Custom-field columns (07.3): each selected field_key becomes a column whose
    // value is looked up from the real custom-fields values map by parcel id.
    // Not server-sortable (values live outside the parcels table), so sort off.
    ...customCols.map((fieldKey): ColumnDef<Parcel> => {
      const def = defByKey.get(fieldKey);
      const valuesForField = cfValuesQuery.data?.[fieldKey];
      return {
        id: `cf:${fieldKey}`,
        header: def?.label ?? fieldKey,
        enableSorting: false,
        cell: ({ row }) => {
          const raw = valuesForField?.[row.original.id];
          if (raw == null || raw === '') return <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
          if (def?.data_type === 'boolean') return raw === 'true' ? 'Yes' : 'No';
          return <span style={{ color: 'var(--color-ink)' }}>{raw}</span>;
        },
      };
    }),
  ], [customCols, defByKey, cfValuesQuery.data]);

  const { query, rows, total } = list;
  const hasFilters = list.search !== '' || statusFilter !== '' || rePpFilter !== '' || appealFilter !== '' || settledFilter !== '' || stateFilter !== '' || countyFilter !== '' || assessorFilter !== '' || assignedFilter !== '';

  return (
    <>
      <PageHeader
        title="Parcels"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'parcel' : 'parcels'}`}
        actions={
          <>
            {availableDefs.length > 0 && (
              <Button variant="secondary" size="md" onClick={() => setShowCustomPicker(v => !v)}>
                Custom columns{customCols.length > 0 ? ` (${customCols.length})` : ''}
              </Button>
            )}
            <SavedViewMenu
              entityType="parcels"
              currentConfig={currentConfig}
              onApply={applyView}
              canShare={canShare}
              currentUserId={meQuery.data?.id}
            />
            <Button variant="primary" size="md" onClick={() => navigate('/parcels/new')}>
              + New parcel
            </Button>
          </>
        }
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search parcels by account #, name, owner, state…"
        filters={[
          { key: 'status', value: statusFilter, onChange: setStatusFilter, placeholder: 'All statuses', options: STATUS_OPTIONS },
          { key: 're_pp', value: rePpFilter, onChange: setRePpFilter, placeholder: 'All types', options: RE_PP_OPTIONS },
          { key: 'under_appeal', value: appealFilter, onChange: setAppealFilter, placeholder: 'Any appeal state', options: APPEAL_OPTIONS },
          { key: 'settled', value: settledFilter, onChange: setSettledFilter, placeholder: 'Any settlement', options: SETTLED_OPTIONS },
          { key: 'state', value: stateFilter, onChange: setStateFilter, placeholder: 'All states', options: STATE_FILTER_OPTIONS },
          { key: 'assessor_id', value: assessorFilter, onChange: setAssessorFilter, placeholder: 'All assessors', options: assessorOptions },
          { key: 'assigned', value: assignedFilter, onChange: setAssignedFilter, placeholder: 'Any assignment', options: ASSIGNED_OPTIONS },
        ]}
      />

      {countyFilter !== '' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          padding: 'var(--sp-2) var(--sp-3)',
          marginBottom: 'var(--sp-4)',
          background: 'var(--brand-primary-bg)',
          border: '1px solid var(--brand-primary)',
          borderRadius: 'var(--r-lg)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-ink)',
        }}>
          <span>
            Showing parcels in <strong>{countyFilter}</strong>
          </span>
          <button
            type="button"
            onClick={() => setCountyFilter('')}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'var(--brand-primary)',
              fontWeight: 'var(--fw-medium)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Clear
          </button>
        </div>
      )}

      {showCustomPicker && availableDefs.length > 0 && (
        <div style={{
          padding: 'var(--sp-4)',
          marginBottom: 'var(--sp-4)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-lg)',
        }}>
          <div style={{
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--ls-wide)',
            marginBottom: 'var(--sp-3)',
          }}>Custom-field columns</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
            gap: 'var(--sp-2)',
          }}>
            {availableDefs.map(def => (
              <Checkbox
                key={def.field_key}
                label={def.label}
                description={def.field_key}
                checked={customCols.includes(def.field_key)}
                onChange={() => toggleCustomCol(def.field_key)}
              />
            ))}
          </div>
        </div>
      )}

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load parcels:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && total === 0 && !query.error && (
        hasFilters ? <NoMatches /> : <EmptyState />
      )}

      {(total > 0 || query.isFetching) && !query.error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/parcels/${row.id}`)}
            // Warm the parcel DETAIL query (ParcelDetail: ['parcel', id] →
            // /api/parcels/:id) on row hover so the click opens a cached page.
            onRowPrefetch={(row) => {
              void queryClient.prefetchQuery({
                queryKey: ['parcel', row.id],
                queryFn: ({ signal }) => api.get<Parcel>(`/api/parcels/${row.id}`, signal),
              });
            }}
            emptyMessage={query.isFetching ? 'Loading…' : 'No parcels'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
    </>
  );
}

function NoMatches() {
  return (
    <div style={{
      padding: 'var(--sp-7)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No parcels match your search or filters.</p>
    </div>
  );
}

interface DuplicateMatch {
  id: string;
  account_number: string;
  name: string | null;
  owner_name: string | null;
  state: string | null;
  county: string | null;
}
interface DuplicateCheckResponse {
  duplicate: boolean;
  count: number;
  matches: DuplicateMatch[];
  // Accounts differing from this one ONLY by leading zeros. Absent when there are
  // none, so an older response shape reads as "no near match" rather than breaking.
  // Deliberately separate from `duplicate`: a near hit is not a confirmed duplicate,
  // and saying "already exists" about one would be false.
  near_match?: boolean;
  near_count?: number;
  near_matches?: DuplicateMatch[];
}

/**
 * ParcelDuplicateWarning — advisory pre-create duplicate check (Domain 02.4).
 *
 * Watches the live parcel-create form values and calls the real
 * GET /api/parcels/check-duplicate endpoint (org + book scoped) as the operator
 * types the account number. If a matching parcel already exists it renders a
 * non-blocking "possible duplicate" banner listing the existing account(s). It
 * never blocks the create — the operator can still save (e.g. a legitimate RE/PP
 * pair on the same account).
 */
export function ParcelDuplicateWarning({ values }: { values: FormValues }) {
  const account = typeof values.account_number === 'string' ? values.account_number.trim() : '';
  const state = typeof values.state === 'string' ? values.state.trim() : '';
  const county = typeof values.county === 'string' ? values.county.trim() : '';

  // Debounce so we query on a settled account number, not every keystroke.
  const [debounced, setDebounced] = useState({ account: '', state: '', county: '' });
  useEffect(() => {
    const t = setTimeout(() => setDebounced({ account, state, county }), 400);
    return () => clearTimeout(t);
  }, [account, state, county]);

  const dupQuery = useQuery<DuplicateCheckResponse>({
    queryKey: ['parcel-duplicate-check', debounced.account, debounced.state, debounced.county],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ account_number: debounced.account });
      if (debounced.state) params.set('state', debounced.state);
      if (debounced.county) params.set('county', debounced.county);
      return api.get<DuplicateCheckResponse>(`/api/parcels/check-duplicate?${params.toString()}`, signal);
    },
    enabled: debounced.account.length > 0,
    staleTime: 30_000,
  });

  const data = dupQuery.data;
  if (!data?.duplicate && !data?.near_match) return null;

  // The near-match case: no account with this exact number exists, but one differs
  // only by leading zeros. Real and routine — a Tarrant statement prints 00042951001
  // for the parcel its appraisal district calls 42951001. It is shown as a QUESTION,
  // not as a duplicate, because the two really can be different accounts.
  if (!data.duplicate && data.near_match) {
    const near = data.near_matches || [];
    return (
      <div style={{
        marginTop: 'var(--sp-4)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-status-amber)',
        borderRadius: 'var(--r-md)',
        color: 'var(--color-ink)',
        fontSize: 'var(--fs-sm)',
      }}>
        <strong style={{ display: 'block', marginBottom: 'var(--sp-1)' }}>
          Is this the same account as one you already have?
        </strong>
        <p style={{ margin: '0 0 var(--sp-1)' }}>
          {debounced.account} differs only by leading zeros from:
        </p>
        <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
          {near.map((m) => (
            <li key={m.id}>
              {m.account_number}
              {m.name ? ` — ${m.name}` : ''}
              {m.owner_name ? ` (${m.owner_name})` : ''}
            </li>
          ))}
        </ul>
        <p style={{ margin: 'var(--sp-2) 0 0', color: 'var(--color-ink-3)' }}>
          Different offices pad account numbers differently. If it is the same parcel, use
          the existing account instead of creating a second one.
        </p>
      </div>
    );
  }

  const matches = data.matches;
  return (
    <div style={{
      marginTop: 'var(--sp-4)',
      padding: 'var(--sp-3) var(--sp-4)',
      background: 'var(--color-status-amber-bg)',
      border: '1px solid var(--color-status-amber)',
      borderRadius: 'var(--r-md)',
      color: 'var(--color-ink)',
      fontSize: 'var(--fs-sm)',
    }}>
      <strong style={{ display: 'block', marginBottom: 'var(--sp-1)' }}>
        Possible duplicate — account {debounced.account} already exists
      </strong>
      <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
        {matches.map((m) => (
          <li key={m.id}>
            {m.account_number}
            {m.name ? ` — ${m.name}` : ''}
            {m.owner_name ? ` (${m.owner_name})` : ''}
            {m.county || m.state ? ` · ${[m.county, m.state].filter(Boolean).join(', ')}` : ''}
          </li>
        ))}
      </ul>
      <p style={{ margin: 'var(--sp-2) 0 0', color: 'var(--color-ink-3)' }}>
        You can still create this parcel if it is intentional.
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      padding: 'var(--sp-8)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink-3)',
      }}>No parcels yet</h2>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
        Create a parcel on a property's detail page (v1 Property Setup wizard) to populate this list.
      </p>
    </div>
  );
}
